import { gql } from 'graphql-request'

const getPage = gql`
  query getPage($slug: String, $lang: [Locale!]!, $assetLocales: [Locale!]) {
    pages(locales: $lang, where: { slug: $slug }) {
      id
      title
      slug
      parentPage {
        slug
        title
      }
      themeColor
      showPageNavigation
      localizations(includeCurrent: true) {
        slug
        locale
      }
      seo {
        metaTitle
        metaDescription
        ogImage(locales: $assetLocales) {
          src: url(
            transformation: {
              image: { resize: { width: 1200, height: 630, fit: crop } }
            }
          )
        }
        noIndex
      }
      elements(locales: $lang) {
        ... on PageContactForm {
          id
          type: __typename
          title
          anchorId {
            value
          }
          text
          fields {
            ... on CustomField {
              id
              title
              required
              options {
                text
              }
              inputType
            }
          }
        }
        ... on PageIntro {
          id
          type: __typename
          title
          showInLayout
          content
          anchorId {
            value
          }
        }
        ... on PageIntroTab {
          id
          type: __typename
          title
          showInLayout
          textSideRight
          anchorId {
            value
          }
          introItems {
            name
            title
            text
            buttons {
              title
              linkTarget
              url
            }
            positionImage
            marginImage
            image {
              url(transformation: { document: { output: { format: webp } } })
            }
          }
        }
        ... on PageWorldwide {
          id
          type: __typename
          title
          anchorId {
            value
          }
          showInLayout
          text {
            html
          }
          ourNumbers {
            label
            value
          }
          buttons {
            linkTarget
            title
            url
          }
          background {
            url(transformation: { document: { output: { format: webp } } })
          }
        }
        ... on PageMentorsJourneyVideo {
          id
          type: __typename
          title
          anchorId {
            value
          }
          showInLayout
          subtitle
          mentorsProfile {
            url(transformation: { document: { output: { format: webp } } })
          }
          infos {
            label
            value
          }
          text
          button {
            linkTarget
            title
            url
          }
          mentorVideoCarousel {
            id
            firstName
            implantType
            slug
            videoLink
            videoThumbnail {
              url(transformation: { document: { output: { format: webp } } })
            }
            avatarForVideo {
              url(transformation: { document: { output: { format: webp } } })
            }
          }
        }
        ... on PageHero {
          id
          type: __typename
          title
          anchorId {
            value
          }
          showInLayout
          content
          image(locales: $assetLocales) {
            src: url(
              transformation: {
                image: { resize: { width: 1220, height: 500, fit: crop } }
                document: { output: { format: webp } }
              }
            )
            url
            mimeType
            alt
            locale
          }
          imageMobile(locales: $assetLocales) {
            src: url(
              transformation: {
                image: { resize: { width: 1220, height: 500, fit: crop } }
                document: { output: { format: webp } }
              }
            )
            url
            mimeType
            alt
            locale
          }
        }
        ... on PageHearingDiary {
          id
          type: __typename
          image: backgroundImage {
            url(
              transformation: {
                document: { output: { format: webp } }
                image: { resize: { fit: crop, height: 382, width: 1221 } }
              }
            )
          }
          buttonText
          title
          showInLayout
          text
          slug
        }
        ... on PageHearingDiaryDownload {
          id
          type: __typename
          title
          showInLayout
          text
          image {
            url(transformation: { document: { output: { format: webp } } })
          }
          floatingItems
          buttonText
          anchorId {
            value
          }
        }
        ... on PageStage {
          id
          type: __typename
          title
          showInLayout
          items: stageElements {
            id
            title
            text
            themeColor
            image(locales: $assetLocales) {
              id
              src: url(
                transformation: {
                  image: { resize: { width: 400, height: 500, fit: crop } }
                  document: { output: { format: webp } }
                }
              )
              alt
              locale
            }
            page {
              slug
              title
            }
          }
        }
        ... on PageCallToAction {
          id
          title
          anchorId {
            value
          }
          showInLayout
          text
          smallText
          type: __typename
          buttonText {
            text
          }
          button: link {
            ... on Link {
              url
            }
          }
          visibility
        }
        ... on PageWhatsappButton {
          id
          title
          showInLayout
          text
          type: __typename
          hideModule
          link {
            url
          }
          anchorId {
            value
          }
        }
        ... on PageViberButton {
          id
          title
          showInLayout
          text
          type: __typename
          hideModule
          link {
            url
          }
          anchorId {
            value
          }
        }
        ... on PageTeaser {
          id
          title
          anchorId {
            value
          }
          showInLayout
          subline
          type: __typename
          variant: type
          containerWidth
          text {
            html
          }
          linkText {
            id
            text
          }
          link {
            ... on Page {
              id
              url: slug
              type: __typename
            }
            ... on Link {
              id
              url
              title
              linkTarget
              type: __typename
            }
            ... on ArticleTopic {
              id
              url: slug
              parentPage {
                slug
              }
              items: article {
                id
              }
              featuredItems: featuredArticles {
                id
              }
            }
          }
          image(locales: $assetLocales) {
            src: url(
              transformation: {
                image: { resize: { width: 600, height: 400, fit: crop } }
              }
            )
            src_webp: url(
              transformation: {
                image: { resize: { width: 600, height: 400, fit: crop } }
                document: { output: { format: webp } }
              }
            )
            alt
            locale
          }
          articles {
            id
            reactions
            slug
            tags {
              title
            }
            parentPage {
              slug
            }
            title
            image {
              alt
              url(transformation: { document: { output: { format: webp } } })
            }
            topics {
              slug
              title
            }
          }
          reels {
            ... on InstagramReel {
              id
              linkInstagram {
                linkTarget
                title
                url
              }
              video {
                url
              }
              videoThumbnail {
                url(transformation: { document: { output: { format: webp } } })
              }
            }
          }
          cards {
            ... on Card {
              id
              text
              title
              image {
                url(transformation: { document: { output: { format: webp } } })
              }
              imageLight {
                url(transformation: { document: { output: { format: webp } } })
              }
              button {
                linkTarget
                title
                url
              }
            }
          }
        }
        ... on PageTextImageFlexible {
          id
          type: __typename
          locale
          showInLayout
          subline
          titleText
          textSide
          boxedLayout
          roundedImage
          backgroundGray
          backgroundImagee {
            id
            url(transformation: { document: { output: { format: webp } } })
          }
          contentLargerThanImage
          paddingInImage
          anchorId {
            value
          }
          buttons {
            linkTarget
            title
            url
          }
          content {
            html
          }
          image {
            url(transformation: { document: { output: { format: webp } } })
          }
        }
        ... on PageCommunitySlider {
          id
          type: __typename
          title
          anchorId {
            value
          }
          showInLayout
          subline
          text
          themeColor
          items {
            title
            subline
            text {
              html
            }
            image {
              url(transformation: { document: { output: { format: webp } } })
            }
            link {
              linkTarget
              title
              url
            }
          }
        }
        ... on PageNewsletterForm {
          id
          type: __typename
          title
          showInLayout
          subline
          anchorId {
            value
          }
          customForm
          imageGrid {
            image1 {
              url(
                transformation: {
                  document: { output: { format: webp } }
                  image: { resize: { height: 205, width: 305, fit: crop } }
                }
              )
            }
            image2 {
              url(
                transformation: {
                  document: { output: { format: webp } }
                  image: { resize: { height: 305, width: 205, fit: crop } }
                }
              )
            }
            image3 {
              url(
                transformation: {
                  document: { output: { format: webp } }
                  image: { resize: { height: 205, width: 305, fit: crop } }
                }
              )
            }
            image4 {
              url(
                transformation: {
                  document: { output: { format: webp } }
                  image: { resize: { height: 305, width: 205, fit: crop } }
                }
              )
            }
            image5 {
              url(
                transformation: {
                  document: { output: { format: webp } }
                  image: { resize: { height: 205, width: 305, fit: crop } }
                }
              )
            }
          }
        }
        ... on PageIntroFlexible {
          id
          type: __typename
          title
          anchorId {
            value
          }
          showInLayout
          sectionType
          subline
          content
          image {
            url(transformation: { document: { output: { format: webp } } })
          }
          placeholderSearch
          buttons {
            linkTarget
            title
            url
          }
          buttonNext
          backgroundColor
          pages(where: { slug: $slug }) {
            slug
            title
          }
        }
        ... on PageAnyQuestion {
          id
          type: __typename
          anchorId {
            value
          }
          showInLayout
          label
          horizontalOrientation
          mentorsProfile {
            url(transformation: { document: { output: { format: webp } } })
          }
          content {
            title
            subline
            image {
              url(transformation: { document: { output: { format: webp } } })
            }
            text {
              text
              html
            }
          }
          buttons {
            linkTarget
            title
            url
          }
        }
        ... on PageFaq {
          id
          type: __typename
          showInLayout
          locale
          anchorId {
            value
          }
          imageSide
          image {
            alt
            url(
              transformation: {
                document: { output: { format: webp } }
                image: { resize: { height: 800, width: 800, fit: clip } }
              }
            )
          }
          title
          description
          faqQuestionsAnswers {
            title: question
            text: answer {
              html
            }
          }
        }
        ... on PageCarousel {
          id
          title
          showInLayout
          text
          carouselType: type
          type: __typename
          link {
            text: title
            url: slug
          }
          items {
            ... on Article {
              id
              link: slug
              title
              shortTitle
              text: excerpt
              tags {
                id
                title
              }
              author {
                ... on Mentor {
                  title: firstName
                }
                ... on Author {
                  title: name
                }
              }
              image(locales: $assetLocales) {
                src: url(
                  transformation: {
                    image: { resize: { width: 785, height: 499, fit: crop } }
                    document: { output: { format: webp } }
                  }
                )
                locale
              }
              quote
              pictureQuote {
                url(
                  transformation: {
                    document: { output: { format: webp } }
                    image: { resize: { fit: crop, height: 69, width: 69 } }
                  }
                )
              }
              carouselHearingJourney {
                title
                shortTitle
                quote
                text
                link {
                  title
                  url
                  linkTarget
                }
                pictureQuote {
                  url(
                    transformation: {
                      document: { output: { format: webp } }
                      image: { resize: { fit: crop, height: 69, width: 69 } }
                    }
                  )
                }
              }
            }
            ... on Page {
              id
              link: slug
              title
            }
            ... on ArticleTopic {
              id
              title
              subline
              text
              link: slug
              image(locales: $assetLocales) {
                src: url(
                  transformation: {
                    image: { resize: { width: 300, height: 225, fit: crop } }
                    document: { output: { format: webp } }
                  }
                )
                locale
              }
            }
            ... on Video {
              id
              youtubeVideoLink
              image {
                alt
                url
              }
            }
          }
        }
        ... on PageComponent {
          id
          title
          anchorId {
            value
          }
          showInLayout
          contentType
          content: text {
            html
          }
          link: page {
            url: slug
          }
          button: linkText {
            text
          }
          type: __typename
        }
        ... on PageService {
          id
          title
          showInLayout
          showTitle
          anchorId {
            value
          }
          type: __typename
          items {
            ... on FaqSet {
              id
              title
              type: __typename
              items: faqs {
                title: question
                text: answer {
                  html
                }
              }
            }
            ... on Download {
              id
              title
              type: __typename
              items {
                id
                size
                title
                text: caption
                alt
                extension: mimeType
                thumbnail: url(
                  transformation: {
                    document: { output: { format: png } }
                    image: { resize: { width: 200, height: 290, fit: crop } }
                  }
                )
                thumbnailWebp: url(
                  transformation: {
                    document: { output: { format: webp } }
                    image: { resize: { width: 200, height: 290, fit: crop } }
                  }
                )
                url
                fileName
              }
            }
          }
        }
      }
    }
    landingpages(locales: $lang, where: { slug: $slug }) {
      id
      slug
      title
      floatingButton {
        icon {
          locale
          url
        }
        link {
          locale
          url
          title
          linkTarget
        }
      }
      elements: modules(locales: $lang) {
        ... on PageContactForm {
          id
          type: __typename
          title
          anchorId {
            value
          }
          text
          fields {
            ... on CustomField {
              id
              title
              required
              options {
                text
              }
              inputType
            }
          }
        }
        ... on PageHero {
          id
          anchorId {
            value
          }
          type: __typename
          title
          content
          image(locales: $assetLocales) {
            src: url(
              transformation: {
                image: { resize: { width: 1220, height: 500, fit: crop } }
                document: { output: { format: webp } }
              }
            )
            mimeType
            alt
            locale
          }
          imageMobile(locales: $assetLocales) {
            src: url(
              transformation: {
                image: { resize: { width: 1220, height: 500, fit: crop } }
                document: { output: { format: webp } }
              }
            )
            mimeType
            alt
            locale
          }
          video(locales: $assetLocales) {
            url
            mimeType
            alt
            locale
          }
          buttonLink {
            url
          }
          buttonText {
            text
          }
        }
        ... on PageCallToAction {
          id
          title
          text
          smallText
          visibility
          type: __typename
          anchorId {
            value
          }
          buttonText {
            text
          }
          button: link {
            ... on Link {
              url
            }
          }
        }
        ... on PageWhatsappButton {
          id
          title
          text
          type: __typename
          hideModule
          link {
            url
          }
          anchorId {
            value
          }
        }
        ... on PageViberButton {
          id
          title
          showInLayout
          text
          type: __typename
          hideModule
          link {
            url
          }
          anchorId {
            value
          }
        }
        ... on PageIntro {
          id
          type: __typename
          title
          content
          anchorId {
            value
          }
          callToActionButton {
            label
            link {
              url
            }
          }
        }
        ... on PageTeaser {
          id
          title
          anchorId {
            value
          }
          showInLayout
          subline
          type: __typename
          variant: type
          containerWidth
          text {
            html
          }
          linkText {
            id
            text
          }
          link {
            ... on Page {
              id
              url: slug
              type: __typename
            }
            ... on Link {
              id
              url
              title
              linkTarget
              type: __typename
            }
            ... on ArticleTopic {
              id
              url: slug
              parentPage {
                slug
              }
              items: article {
                id
              }
              featuredItems: featuredArticles {
                id
              }
            }
          }
          image(locales: $assetLocales) {
            src: url(
              transformation: {
                image: { resize: { width: 600, height: 400, fit: crop } }
              }
            )
            src_webp: url(
              transformation: {
                image: { resize: { width: 600, height: 400, fit: crop } }
                document: { output: { format: webp } }
              }
            )
            alt
            locale
          }
          articles {
            id
            reactions
            slug
            tags {
              title
            }
            parentPage {
              slug
            }
            title
            image {
              alt
              url(transformation: { document: { output: { format: webp } } })
            }
            topics {
              slug
              title
            }
          }
          reels {
            ... on InstagramReel {
              id
              linkInstagram {
                linkTarget
                title
                url
              }
              video {
                url
              }
              videoThumbnail {
                url(transformation: { document: { output: { format: webp } } })
              }
            }
          }
          cards {
            ... on Card {
              id
              text
              title
              image {
                url(transformation: { document: { output: { format: webp } } })
              }
              imageLight {
                url(transformation: { document: { output: { format: webp } } })
              }
              button {
                linkTarget
                title
                url
              }
            }
          }
        }
        ... on PageBenefit {
          id
          type: __typename
          title
          anchorId {
            value
          }
          benefit1 {
            text
            image {
              url
              alt
              caption
            }
          }
          benefit2 {
            text
            image {
              url
              alt
              caption
            }
          }
          benefit3 {
            text
            image {
              url
              alt
              caption
            }
          }
          callToActionButton {
            label
            link {
              url
            }
          }
        }
        ... on PageTestimonial {
          id
          type: __typename
          quote
          author
          authorImage {
            url
            alt
            caption
          }
          anchorId {
            value
          }
          authorLocalization
          countryCode
          description
        }
        ... on PageTestimonialCarousel {
          id
          type: __typename
          headline
          anchorId {
            value
          }
          pageTestimonials {
            id
            quote
            author
            authorImage {
              alt
              src: url(
                transformation: {
                  image: { resize: { width: 64, height: 64, fit: crop } }
                }
              )
              src_webp: url(
                transformation: {
                  image: { resize: { width: 64, height: 64, fit: crop } }
                  document: { output: { format: webp } }
                }
              )
            }
            authorLocalization
            countryCode
            description
          }
          callToActionButton {
            label
            link {
              url
            }
          }
        }
        ... on PageMentorsForm {
          id
          type: __typename
          title
          newLayout
          anchorId {
            value
          }
        }
        ... on PageNewsletterForm {
          id
          type: __typename
          title
          subline
          anchorId {
            value
          }
        }
        ... on PageTextBanner {
          id
          type: __typename
          locale
          showInLayout
          textSide
          title
          content
          headline
          image {
            url(
              transformation: {
                image: { resize: { fit: clip, height: 500, width: 1220 } }
                document: { output: { format: webp } }
              }
            )
          }
          primaryButtonLink {
            title
            url
            linkTarget
          }
          textLink {
            linkTarget
            title
            url
          }
          anchorId {
            value
          }
        }
        ... on PageTextImage {
          id
          type: __typename
          locale
          showInLayout
          textSide
          content {
            html
          }
          image {
            url(
              transformation: {
                image: { resize: { fit: clip, height: 800, width: 800 } }
                document: { output: { format: webp } }
              }
            )
          }
          buttonLink {
            title
            url
            linkTarget
          }
          anchorId {
            value
          }
          titleText: title
          boxedLayout
          textLink {
            title
            url
            linkTarget
          }
        }
        ... on PageFullVideo {
          id
          type: __typename
          locale
          showInLayout
          titleText: title
          shortDescription
          video {
            youtubeVideoLink
            image {
              alt
              url
            }
            id
          }
          anchorId {
            value
          }
        }
        ... on PageGridMovie {
          id
          type: __typename
          locale
          showInLayout
          title
          shortDescription
          gridVideos {
            title
            description
            video {
              youtubeVideoLink
              id
              image {
                url(
                  transformation: {
                    image: { resize: { fit: clip, height: 300, width: 530 } }
                  }
                )
                alt
              }
            }
          }
          anchorId {
            value
          }
        }
        ... on PageGridIcon {
          id
          type: __typename
          locale
          showInLayout
          title: gridIconTitle
          anchorId {
            value
          }
          iconContent {
            description {
              html
            }
            title
            locale
            link {
              url
              linkTarget
              title
            }
            icon {
              url
            }
          }
        }
        ... on PageTestimonialSingleSlider {
          id
          type: __typename
          headline
          locale
          anchorId {
            value
          }
          pageTestimonials {
            id
            quote
            author
            authorImage {
              alt
              src: url(
                transformation: {
                  image: { resize: { width: 150, height: 150, fit: crop } }
                }
              )
              src_webp: url(
                transformation: {
                  image: { resize: { width: 150, height: 150, fit: crop } }
                  document: { output: { format: webp } }
                }
              )
            }
            authorLocalization
            countryCode
            description
          }
        }
        ... on PageFaq {
          id
          type: __typename
          locale
          anchorId {
            value
          }
          imageSide
          image {
            alt
            url(
              transformation: {
                document: { output: { format: webp } }
                image: { resize: { height: 800, width: 800, fit: clip } }
              }
            )
          }
          title
          description
          faqQuestionsAnswers {
            title: question
            text: answer {
              html
            }
          }
        }
        ... on PageComponent {
          id
          title
          anchorId {
            value
          }
          contentType
          content: text {
            html
          }
          link: page {
            url: slug
          }
          button: linkText {
            text
          }
          type: __typename
        }
        ... on PageShortForm {
          id
          type: __typename
          locale
          anchorId {
            value
          }
          description
          title
          enableShortForm
        }
      }
    }
  }
`
export default getPage
